// src/router/index.js
import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import Layout from "@/pages/layout/layout.vue";

Vue.use(Router);

// 安静处理重复点击路由报错
const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};

/**
 * withAuthModal(pagePath, pageComponentFn, routeName)
 * - pagePath: 子路由路径（相对父路由 '/'），home 使用 ""
 * - pageComponentFn: () => import('...') 懒加载函数
 * - routeName: 唯一路由名 (string)
 *
 * 返回值是三个路由对象：page、page + /login、page + /register
 */
function withAuthModal(pagePath, pageComponentFn, routeName) {
  // 处理空 path 的情况 (首页)
  const basePath = pagePath === "" ? "" : pagePath;
  const loginPath = basePath ? `${basePath}/login` : "login";
  const registerPath = basePath ? `${basePath}/register` : "register";

  return [
    {
      path: basePath,         // '' 或 'promotions' 等
      name: routeName,
      component: pageComponentFn
    },
    {
      path: loginPath,        // 'login' 或 'promotions/login'
      name: `${routeName}_login`,
      components: {
        default: pageComponentFn,
        modal: () => import("@/pages/components/login.vue")
      }
    },
    {
      path: registerPath,     // 'register' 或 'promotions/register'
      name: `${routeName}_register`,
      components: {
        default: pageComponentFn,
        modal: () => import("@/pages/components/register.vue")
      }
    }
  ];
}

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      component: Layout,
      children: [
        // 首页（/）
        ...withAuthModal("", () => import("@/pages/home/homePage.vue"), "homePage"),

        // promotions + modal
        ...withAuthModal(
          "promotions",
          () => import("@/pages/promotionsPage/promotionsPage.vue"),
          "promotions"
        ),

        // promotions 详情（单独路由）
        {
          path: "promotionsDetails/:id",
          name: "promotionsDetails",
          component: () => import("@/pages/promotionsDetails/index.vue")
        },

        // 游戏页
        ...withAuthModal(
          "gamePage",
          () => import("@/pages/gamePage/gamePage.vue"),
          "gamePage"
        ),

        // 下载页
        ...withAuthModal("down", () => import("@/pages/down/index.vue"), "down"),

        // 设置页
        ...withAuthModal(
          "setpage",
          () => import("@/pages/setPage/setPage.vue"),
          "setpage"
        ),

        // 常见问题
        ...withAuthModal(
          "faqpage",
          () => import("@/pages/faqPage/faqPage.vue"),
          "faqpage"
        ),

        // 签到
        ...withAuthModal(
          "SignInPage",
          () => import("@/pages/SignInPage/SignInPage.vue"),
          "SignInPage"
        ),

        // 投注转盘
        ...withAuthModal(
          "BettingSpinPage",
          () => import("@/pages/BettingSpinPage/BettingSpinPage.vue"),
          "BettingSpinPage"
        ),

        // 每日幸运转盘
        ...withAuthModal(
          "DailyLuckySpinPage",
          () => import("@/pages/DailyLuckySpinPage/DailyLuckySpinPage.vue"),
          "DailyLuckySpinPage"
        ),

        // 存款转盘
        ...withAuthModal(
          "DepositSpinPage",
          () => import("@/pages/DepositSpinPage/DepositSpinPage.vue"),
          "DepositSpinPage"
        ),

        // content
        ...withAuthModal("content", () => import("@/pages/content/index.vue"), "content"),

        // wallet
        ...withAuthModal("wallet", () => import("@/pages/wallet/wallet.vue"), "wallet"),

        // referral
        ...withAuthModal("referral", () => import("@/pages/referral/referral.vue"), "referral"),

        // bankAccount
        ...withAuthModal("bankAccount", () => import("@/pages/bankAccount/bankAccount.vue"), "bankAccount"),

        // 支付回调（单独）
        {
          path: "paycallback",
          name: "paycallback",
          component: () => import("@/pages/payCallback/payCallback.vue")
        },

        // 为兼容旧代码（如果你的代码里有直接通过 name: 'login' 或 name: 'register' 去跳转的）
        // 我在根下再额外放一个全局 login/register（覆盖的是首页 + modal）
        {
          path: "login",
          name: "login", // 兼容旧调用 router.push({ name: 'login' })
          components: {
            default: () => import("@/pages/layout/layout.vue"),
            modal: () => import("@/pages/components/login.vue")
          }
        },
        {
          path: "register",
          name: "register", // 兼容旧调用 router.push({ name: 'register' })
          components: {
            default: () => import("@/pages/layout/layout.vue"),
            modal: () => import("@/pages/components/register.vue")
          }
        }
      ]
    },

    // catch-all
    {
      path: "*",
      redirect: "/"
    }
  ]
});

// 全局前置守卫（保留你之前的逻辑）
router.beforeEach((to, from, next) => {
  let isLogin = localStorage.getItem("token");
  store.dispatch("updateCurrentRoute", to);
  if (isLogin) {
    next();
  } else {
    // 根据你之前注释的逻辑，开放直接访问登录页（login modal）
    next();
  }
});

export default router;
